import { Injectable, Injector, Type } from '@angular/core';
import 'navigator.locks';
import { TimeoutError } from 'rxjs';
import { GlobalConstants } from '../constant/constant';
import { SyncActionTypeData, SyncActionTypeId, SyncActionTypes } from '../constant/sync-action-constants';
import { StoreName } from '../entities/StoreName';
import { DbTransaction } from '../models/db/db-transaction';
import { CompletedSyncAction, SavedSyncAction, SyncAction, SyncActionAttempt, SyncActionStatus, UnsavedSyncAction } from '../models/sync-action/sync-action-interface';
import { ServiceInjections, ServiceNames, SyncActionType } from '../models/sync-action/sync-action-types-interface';
import { ActivityService } from '../services/activity.service';
import { ActivityLogService } from '../services/activityLog.service';
import { DataService } from '../services/data.service';
import { EventBusService } from '../services/event-bus.service';
import { TimeRecordService } from '../services/timeRecord.service';
import { DateTimeHelper } from '../utils/date-time-helper';
import { ConflictValidationException } from '../utils/exceptions';
import { HttpException, NetworkException, ProcessLockException } from '../utils/exceptions/mobile-exceptions';
import { RepositoryService } from './repository.service';
import { NotificationsService } from '../services/notifications.service';
import { TimeSyncService } from '../services/time-sync.service';
import { AppEventType } from '../enums/app-events.enum';
import { OdometerReadingService } from '../services/odometer-reading.service';
import { LogService } from './logging/log.service';

@Injectable({
  providedIn: 'root',
})
export class SyncActionService extends DataService {
  private SERVICE_MAP = new Map<ServiceNames, Type<any>>([
    [ServiceNames.ActivityLogService, ActivityLogService],
    [ServiceNames.TimeRecordService, TimeRecordService],
    [ServiceNames.ActivityService, ActivityService],
    [ServiceNames.RepositoryService, RepositoryService],
    [ServiceNames.EventBusService, EventBusService],
    [ServiceNames.TimeSyncService, TimeSyncService],
    [ServiceNames.OdometerReadingService, OdometerReadingService],
    // add other services as needed
  ]);

  private failedActionsToNotify: SavedSyncAction<any>[] = [];

  constructor(
    private injector: Injector,
    private notificationService: NotificationsService, // TODO: Remove dependancy
    private eventBusService: EventBusService,
    private logService: LogService,
  ) {
    super();
  }

  /**
   * Initialize a sync action object with the specified type ID and payload.
   * Note that this does not store the action in the database or queue the action.
   * @template T - The type of the sync action.
   * @param typeId - The type ID of the sync action.
   * @param params - The URL parameters of the sync action.
   * @param auxillaryData - The auxiliary data of the sync action.
   * @param payload - The payload of the sync action.
   * @returns An unsaved sync action object.
   */
  private buildAction<T extends SyncActionTypeId>(typeId: T, params: SyncActionTypeData[T]["params"], auxillaryData: SyncActionTypeData[T]["auxillaryData"], payload: SyncActionTypeData[T]["payload"]): UnsavedSyncAction<T> {
    return {
      typeId: typeId,
      params: params,
      auxillaryData: auxillaryData,
      payload: payload,
      status: SyncActionStatus.Queued,
      createdDate: DateTimeHelper.getUtcNowString(),
      attempts: [],
    };
  }

  /**
   * Queues a sync action for execution by storing it in the SyncActionQueue table.
   * @param action - The unsaved sync action to be queued.
   * @param existingTransaction - An optional existing database transaction to use.
   * @returns A promise that resolves to the record ID of the queued action.
   */
  private async queueAction(action: UnsavedSyncAction<SyncActionTypeId>, existingTransaction?: DbTransaction): Promise<number> {
    const recordId = await this.repository.create(action, StoreName.SyncActionQueue, existingTransaction);

    const savedAction = await this.getActionById(recordId, existingTransaction);
    this.eventBusService.emit({ type: AppEventType.SYNC_ACTION_QUEUED, data: savedAction });

    return recordId;
  }

  /**
   * Creates a sync action of the specified type and adds it to the queue.
   * @template T - The type of the sync action.
   * @param {T} typeId - The type identifier of the sync action.
   * @param {AuxillaryDataMap[T]} auxillaryData - The auxillary data of the sync action.
   * @param {PayloadMap[T]} payload - The payload of the sync action.
   * @param {DbTransaction} [existingTransaction] - An optional existing database transaction.
   * @returns {Promise<number>} - A promise that resolves to the ID of the created sync action record.
   */
  public async createAction<T extends SyncActionTypeId>(typeId: T, params: SyncActionTypeData[T]["params"], auxillaryData: SyncActionTypeData[T]["auxillaryData"], payload: SyncActionTypeData[T]["payload"], existingTransaction?: DbTransaction): Promise<number> {
    const action = this.buildAction(typeId, params, auxillaryData, payload);
    const recordId = await this.queueAction(action, existingTransaction);
    return recordId;
  }

  /**
   * Updates a sync action in the sync action queue.
   * @param action - The sync action to update.
   * @param existingTransaction - An optional existing database transaction to use.
   * @returns A promise that resolves when the update is complete.
   */
  private async updateAction(action: SavedSyncAction<any>, existingTransaction?: DbTransaction): Promise<void> {
    await this.repository.update(action, StoreName.SyncActionQueue, existingTransaction);
  }

  /**
   * Retrieves a sync action by its ID.
   * @param recordId - The ID of the sync action to retrieve.
   * @param existingTransaction - An optional existing database transaction to use.
   * @returns A promise that resolves to the sync action with the specified ID.
   */
  async getActionById(recordId: number, existingTransaction?: DbTransaction): Promise<SavedSyncAction<any>> {
    return await this.repository.getByLocalId(StoreName.SyncActionQueue, recordId, undefined, existingTransaction);
  }

  /**
   * Retrieves the count of actions in the queue.
   * @param existingTransaction - Optional existing database transaction.
   * @returns A promise that resolves to the number of actions in the queue.
   */
  async getCountOfActionsInQueue(existingTransaction?: DbTransaction): Promise<number> {
    const actionsInQueue = await this.getActionsInQueue(existingTransaction);
    return actionsInQueue.length;
  }

  /**
   * Retrieves all the sync actions in the queue (i.e., not yet resolved).
   * @param existingTransaction - Optional existing database transaction.
   * @returns A promise that resolves to an array of `SyncAction` objects.
   */
  async getActionsInQueue(existingTransaction?: DbTransaction): Promise<SavedSyncAction<any>[]> {
    return await this.repository.getAllByIndex(StoreName.SyncActionQueue, GlobalConstants.KEY_NAMES.Status, SyncActionStatus.Queued, undefined, existingTransaction);
  }

  /**
   * Retrieves the next action in the queue.
   * @returns A Promise that resolves to the next action in the queue, or null if the queue is empty.
   */
  async getNextActionInQueue(): Promise<SavedSyncAction<any>> {
    const actionsInQueue = await this.getActionsInQueue();

    if (actionsInQueue.length === 0) {
      return null;
    } else {
      const nextActionInQueue = actionsInQueue.reduce((oldestAction, currentAction) => {
        return currentAction.createdDate < oldestAction.createdDate ? currentAction : oldestAction;
      }, actionsInQueue[0]);

      return nextActionInQueue;
    }
  }

  /**
   * Lookup the action type of a given SyncAction.
   * @param action The SyncAction for which to retrieve the action type.
   * @returns The action type of the SyncAction.
   */
  getActionType(action: SyncAction<any>): SyncActionType<SyncActionTypeId> {
    return SyncActionTypes[action.typeId];
  }

  /**
   * Returns the number of attempts made for a sync action.
   * @param action - The sync action for which to get the attempt count.
   * @returns The number of attempts made for the sync action.
   */
  getAttemptCount(action: SavedSyncAction<any>): number {
    return action.attempts.length;
  }

  /**
   * Retrieves the current attempt of a sync action.
   * If there are no attempts or the last attempt has already been resolved, returns null.
   * Otherwise, returns the current unresolved attempt.
   * @param action - The sync action for which to retrieve the current attempt.
   * @returns The current attempt of the sync action, or null if there are no attempts or the last attempt has already been resolved.
   */
  getCurrentAttempt(action: SavedSyncAction<any>): SyncActionAttempt | null {
    const attemptCount = this.getAttemptCount(action);
    if (attemptCount === 0) {
      return null;
    }

    const lastAttempt = action.attempts[attemptCount - 1];
    if (lastAttempt.outcome) {
      return null;
    }

    return lastAttempt;
  }

  /**
   * Adds an attempt to the given SyncAction.
   * @param action - The SyncAction to add the attempt to.
   * @returns The SyncAction with the added attempt.
   */
  private addAttempt(action: SavedSyncAction<any>): SavedSyncAction<any> {
    action.attempts.push({ attemptedDate: DateTimeHelper.getUtcNowString() });
    return action;
  }

  /**
   * Sets the outcome for the current attempt of a sync action.
   * @param action - The sync action for which to set the outcome.
   * @param outcome - The outcome to set for the current attempt.
   * @returns The updated sync action with the outcome set for the current attempt.
   * @throws Error if the action has no attempts.
   */
  private setCurrentAttemptOutcome(action: SavedSyncAction<any>, outcome: string): SavedSyncAction<any> {
    const attemptCount = this.getAttemptCount(action);
    if (attemptCount === 0) {
      throw Error('Cannot set outcome for an action with no attempts.');
    }

    action.attempts[attemptCount - 1].outcome = outcome;
    return action;
  }

  /**
   * Cancels the current attempt of a sync action.
   * Canceling an attempt removes the last attempt from the action's attempts array and does not count it towards the total attempts.
   * @param action - The sync action to cancel the current attempt for.
   * @returns The sync action with the current attempt canceled.
   * @throws Error if the action has no attempts or if the current attempt already has an outcome.
   */
  private cancelCurrentAttempt(action: SavedSyncAction<any>): SavedSyncAction<any> {
    const attemptCount = this.getAttemptCount(action);
    if (attemptCount === 0) {
      throw Error('Cannot cancel attempt for an action with no attempts.');
    }

    const lastAttempt = action.attempts[attemptCount - 1];
    if (lastAttempt.outcome) {
      throw Error('Cannot cancel an attempt that already has an outcome.');
    }

    action.attempts.pop();
    return action;
  }

  /**
   * Checks if the attempt exceeds the timeout for a sync action.
   * @param attempt - The sync action attempt to check.
   * @param actionType - The type of sync action.
   * @returns A boolean indicating whether the attempt exceeds the timeout.
   */
  checkAttemptExceedsTimeout(attempt: SyncActionAttempt, actionType: SyncActionType<SyncActionTypeId>): boolean {
    return !DateTimeHelper.checkTimeDifferenceLessThan('second', actionType.attemptTimeout, attempt.attemptedDate);
  }

  /**
   * Checks if the number of attempts for a sync action exceeds the maximum attempt limit.
   * @param action - The sync action to check.
   * @param actionType - The type of sync action.
   * @returns A boolean indicating whether the number of attempts exceeds the maximum attempt limit.
   */
  checkActionExceedsAttemptLimit(action: SavedSyncAction<any>, actionType: SyncActionType<SyncActionTypeId>): boolean {
    return this.getAttemptCount(action) >= actionType.maxAttempts;
  }

  /**
   * Starts processing the action queue.
   * The action queue will be processed in order, starting with the oldest action.
   * Only one instance of the action queue can be processed at a time.
   * @param waitIfProcessing Specifies whether to wait if the action queue is already being processed. If set to `false`, an error will be thrown if the action queue is already being processed.
   * @param processUpTo Specifies the ID to process the queue up to. The queue will be processed up to this ID and then stop.
   * @returns A Promise that resolves when the action queue has been processed.
   * @throws {ProcessLockException} If the action queue is already being processed and `waitIfProcessing` is set to `false`.
   */
  async startProcessingActionQueue(waitIfProcessing: boolean = true, processUpTo?: number): Promise<void> {
    await navigator.locks.request('processActionQueue', { ifAvailable: !waitIfProcessing }, async lock => {
      if (!lock) {
        throw new ProcessLockException('Action queue is already processing.');
      }

      await this.processActionQueue(processUpTo);
    });

    this.notifyUserOfFailedActions();
  }

  /**
   * Processes the action queue.
   * Note that this is recursive and will continue processing the queue until it is empty or until the specified record ID is reached.
   * @param processUpTo Specifies the ID to process the queue up to. The queue will be processed up to this ID and then stop.
   * @returns A Promise that resolves when the action queue has been processed.
   * @throws {NetworkException} If a network error occurs while processing the action queue, stops processing the queue.
   */
  private async processActionQueue(processUpTo?: number): Promise<void> {
    const nextActionInQueue = await this.getNextActionInQueue();
    if (nextActionInQueue) {
      await this.startProcessingAction(nextActionInQueue.RecordId, true).catch(error => {
        if (error instanceof NetworkException) {
          throw error; // If a network error occurs, stop trying to process the queue
        }
      });

      const completedSyncAction = await this.getActionById(nextActionInQueue.RecordId) as CompletedSyncAction<any>;
      this.eventBusService.emit({ type: AppEventType.SYNC_ACTION_COMPLETED, data: completedSyncAction });

      if (processUpTo && nextActionInQueue.RecordId === processUpTo) {
        return; // Processed up to the specified record ID
      }

      await this.processActionQueue(processUpTo); // Continue processing the queue
    }
  }

  /**
   * Starts processing an action with the specified ID.
   * Note that this method should only be called directly if the action does not have dependencies on other actions and can be processed independently. Otherwise, use `startProcessingActionQueue` to process the action queue.
   * @param actionRecordId - The ID of the action record to process.
   * @param waitIfProcessing Specifies whether to wait if the action is already being processed. If set to `false`, an error will be thrown if the action is already being processed.
   * @throws {ProcessLockException} If the action is already being processed and `waitIfProcessing` is set to false.
   * @returns A Promise that resolves when the action has been processed.
   */
  async startProcessingAction(actionRecordId: number, waitIfProcessing: boolean = true): Promise<void> {
    await navigator.locks.request(`processingAction_${actionRecordId}`, { ifAvailable: !waitIfProcessing }, async lock => {
      if (!lock) {
        throw new ProcessLockException(`Action ${actionRecordId} is already processing.`);
      }

      const action = await this.getActionById(actionRecordId);
      await this.processAction(action);
    });
  }

  /**
   * Processes a sync action.
   * Note that this method is recursive and will repeatedly attempt to process the action until it is successful or fails.
   * @param action - The sync action to be processed.
   * @returns A promise that resolves when the action processing is complete.
   */
  private async processAction(action: SavedSyncAction<any>): Promise<void> {
    if (action.status !== SyncActionStatus.Queued) {
      return; // Action is already completed
    }

    const currentAttempt = this.getCurrentAttempt(action);
    const actionType = this.getActionType(action);

    if (!currentAttempt) {
      await this.attemptAction(action, actionType); // New attempt
      return;
    }

    if (!this.checkAttemptExceedsTimeout(currentAttempt, actionType)) {
      await new Promise(resolve => setTimeout(resolve, 100));
      await this.processAction(action); // Current attempt has not exceeded the timeout limit, so wait and check again.
      return;
    }

    await this.cancelAttemptAndRetryAction(action); // Current attempt has exceeded the timeout limit (though did not http timeout), so cancel and retry.
  }

  /**
   * Attempts to perform a sync action.
   * @param action - The sync action to be performed.
   * @param actionType - The type of sync action.
   * @returns A promise that resolves when the action is completed.
   * @throws {NetworkException} If the request fails due to a network error.
   * @throws {ConflictValidationException} If the request fails with a conflict status code.
   * @throws {HttpException} If the request fails with a non-conflict status code.
   * @throws {TimeoutError} If the request times out.
   * @throws {Error} If an unknown error occurs.
   */
  private async attemptAction(action: SavedSyncAction<any>, actionType: SyncActionType<SyncActionTypeId>): Promise<void> {
    action = this.addAttempt(action);
    await this.updateAction(action);

    try {
      if (actionType.doBefore) {
        await this.executeDoBefore(action, actionType);
      }

      const url = this.http.buildUrl(actionType.url, action.params);
      const method = actionType.method;
      const requestTimeout = actionType.attemptTimeout;
      const payload = action.payload;

      var response = await this.http.request(method, url, payload, requestTimeout).toPromise();

      await this.resolveAction(action, actionType, response);
    } catch (e) {
      if (e instanceof NetworkException) {
        action = this.cancelCurrentAttempt(action);
        await this.updateAction(action);
        throw e;
      } else if (e instanceof HttpException || e instanceof ConflictValidationException) {
        await this.rejectAction(action, actionType, e);
      } else if (e instanceof TimeoutError) {
        await this.retryOrFailAction(action, actionType, e);
      } else {
        await this.retryOrFailAction(action, actionType, e);
      }
    }
  }

  /**
   * Cancels the current attempt of a sync action, updates the action, and retries the action.
   * Only called when the current attempt has exceeded the timeout limit but did not http timeout. This may occur if the app is closed mid-attempt.
   * @param action - The sync action to be canceled and retried.
   * @returns A promise that resolves when the action is canceled and retried.
   */
  private async cancelAttemptAndRetryAction(action: SavedSyncAction<any>): Promise<void> {
    action = this.cancelCurrentAttempt(action);
    await this.updateAction(action);
    await this.processAction(action);
  }

  /**
   * Retries or fails a sync action based on the attempt limit.
   * @param action - The sync action to be retried or failed.
   * @param actionType - The type of sync action.
   * @param error - The error that occurred during the previous attempt (optional).
   * @returns A promise that resolves when the action is retried or failed.
   */
  private async retryOrFailAction(action: SavedSyncAction<any>, actionType: SyncActionType<SyncActionTypeId>, error?: Error): Promise<void> {
    if (!this.checkActionExceedsAttemptLimit(action, actionType)) {
      action = this.setCurrentAttemptOutcome(action, error.message);
      await this.updateAction(action);
      await this.processAction(action); // Retry the action if it has not exceeded the attempt limit.
    } else {
      await this.rejectAction(action, actionType, error); // Set the action as failed if it has exceeded the attempt limit.
    }
  }

  /**
   * Resolves a sync action by executing the onSuccess function and then updating the action status.
   * If an error occurs during the onSuccess function, the action will be retried or failed.
   * @param action - The sync action to resolve.
   * @param actionType - The type of sync action.
   * @param response - The response received from the onSuccess function.
   * @returns A promise that resolves when the action is successfully resolved.
   */
  private async resolveAction(action: SavedSyncAction<any>, actionType: SyncActionType<SyncActionTypeId>, response: any): Promise<void> {
    try {
      action = this.setCurrentAttemptOutcome(action, 'Success');

      const completedAction: CompletedSyncAction<any> = action as CompletedSyncAction<any>;
      completedAction.response = response;
      completedAction.completedDate = DateTimeHelper.getUtcNowString();
      completedAction.status = SyncActionStatus.Success;

      await this.executeOnSuccess(completedAction, actionType).catch(e => {
        this.logService.error('SyncAction.OnSuccessError', e, { syncActionRecordId: action.RecordId, actionType: action.typeId });
        throw e;
      });

      await this.updateAction(completedAction).catch(e => {
        this.logService.error('SyncAction.UpdateError', e, { syncActionRecordId: action.RecordId, actionType: action.typeId });
        throw e;
      });

      this.logService.info('SyncAction.ActionSucceeded', { syncActionRecordId: action.RecordId, actionType: action.typeId, response });
    } catch (e) {
      await this.retryOrFailAction(action, actionType, e);
    }
  }

  /**
   * Rejects a sync action by executing the onFailure function and then updating the action status.
   * @param action - The sync action to be rejected.
   * @param actionType - The type of the sync action.
   * @param error - The error that caused the rejection.
   * @param response - The response associated with the rejection (optional).
   * @returns A promise that resolves when the rejection process is complete.
   * @throws The error that caused the rejection.
   */
  private async rejectAction(action: SavedSyncAction<any>, actionType: SyncActionType<SyncActionTypeId>, error: Error, response?: any): Promise<void> {
    action = this.setCurrentAttemptOutcome(action, error.message);

    const completedAction: CompletedSyncAction<any> = action as CompletedSyncAction<any>;
    completedAction.response = response;
    completedAction.completedDate = DateTimeHelper.getUtcNowString();
    completedAction.status = SyncActionStatus.Failed;

    await this.executeOnFailure(completedAction, actionType).catch(e => {
      this.logService.error('SyncAction.OnFailureError', e, { syncActionRecordId: action.RecordId, actionType: action.typeId });
    });

    this.addFailedActionToNotify(action);

    await this.updateAction(completedAction).catch(e => {
      this.logService.error('SyncAction.UpdateError', e, { syncActionRecordId: action.RecordId, actionType: action.typeId });
    });

    this.logService.warn('SyncAction.ActionFailed', { syncActionRecordId: action.RecordId, actionType: action.typeId, error, response });
    throw error;
  }

  /**
   * Executes the doBefore function of a SyncActionType.
   * @param action - The SyncAction object.
   * @param actionType - The SyncActionType object.
   * @returns A Promise that resolves when the doBefore function is executed.
   */
  private async executeDoBefore(action: SavedSyncAction<any>, actionType: SyncActionType<SyncActionTypeId>): Promise<void> {
    const serviceInjections = await this.getServiceInjections(actionType);
    await actionType.doBefore(serviceInjections, action);
  }

  /**
   * Executes the onSuccess callback of a SyncActionType.
   * @param action - The SyncAction object.
   * @param actionType - The SyncActionType object.
   * @returns A Promise that resolves when the onSuccess callback is executed.
   */
  private async executeOnSuccess(action: CompletedSyncAction<any>, actionType: SyncActionType<SyncActionTypeId>): Promise<void> {
    const serviceInjections = await this.getServiceInjections(actionType);
    await actionType.onSuccess(serviceInjections, action);
  }

  /**
   * Executes the onFailure callback of a SyncActionType.
   * @param action - The action to be executed.
   * @param actionType - The type of the action.
   * @returns A promise that resolves when the onFailure callback is executed.
   */
  private async executeOnFailure(action: CompletedSyncAction<any>, actionType: SyncActionType<SyncActionTypeId>): Promise<void> {
    const serviceInjections = await this.getServiceInjections(actionType);
    await actionType.onFailure(serviceInjections, action);
  }

  /**
   * Retrieves the service injections needed to execute the onSuccess or onFailure callbacks of a SyncActionType.
   * If a SyncActionType is added that requires additional services, they must first be added to this.SERVICE_MAP.
   * @param actionType - The sync action type for which to retrieve the service injections.
   * @returns An object containing the service injections.
   */
  private getServiceInjections(actionType: SyncActionType<SyncActionTypeId>): ServiceInjections {
    const serviceInjections: ServiceInjections = {};

    for (const serviceName of actionType.serviceDependencies) {
      const serviceType = this.SERVICE_MAP.get(serviceName);
      if (serviceType) {
        serviceInjections[serviceName as keyof ServiceInjections] = this.injector.get(serviceType);
      }
    }

    return serviceInjections;
  }

  private addFailedActionToNotify(action: SavedSyncAction<any>): void {
    this.failedActionsToNotify.push(action);
  }

  /**
   * Notifies the user of any failed sync actions.
   * @param failedActions - The list of failed sync actions to notify the user about.
   */
  private notifyUserOfFailedActions(): void {
    const failedActions = [...this.failedActionsToNotify];

    if (failedActions.length > 0) {
      let actionDescriptionList = '';

      for (const action of failedActions) {
        const actionType = this.getActionType(action);
        const description = actionType.description;
        actionDescriptionList += `<li>${description}</li>`;
      }

      this.notificationService.showAlert({
        header: 'Sync Error',
        message: `<p>The below actions failed to sync and have been undone.</p>
          <ul>${actionDescriptionList}</ul>
          <p>If these actions are still valid, please try them again.</p>
        `,
        buttons: ['OK'],
      });

      this.failedActionsToNotify = this.failedActionsToNotify.filter(action => !failedActions.includes(action));
    }
  }
}
